import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ArrowLeft, Download, CheckCircle2, AlertTriangle, FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import AdminLayout from "../../components/AdminLayout";
import { getAdminResults, downloadAdminPdf } from "../../lib/adminApi";
import { toast } from "sonner";

import { OPTION_COLORS } from "../../components/results/constants";
import { getOptionsArray } from "../../components/results/dataUtils";
import { ResultsTabs } from "../../components/results/ResultsTabs";
import { GeneralTab } from "../../components/results/GeneralTab";
import { SexTab } from "../../components/results/SexTab";
import { AgeTab } from "../../components/results/AgeTab";
import { SummaryTab } from "../../components/results/SummaryTab";
import { ParticipationTab } from "../../components/results/ParticipationTab";
import { BlockchainTab } from "../../components/results/BlockchainTab";
import { exportToExcel } from "../../components/results/exportHandlers";

export default function AdminResultsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [results, setResults] = useState(null);
  const [consulta, setConsulta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('general');
  const [visitedTabs, setVisitedTabs] = useState(new Set(['general']));
  const [optionColorMap, setOptionColorMap] = useState({});
  const [exportingPdf, setExportingPdf] = useState(false);

  const loadData = async () => {
    try {
      const res = await getAdminResults(id);
      setResults(res.data);
      setConsulta(res.data.consulta);
    } catch {
      toast.error(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [id]);

  useEffect(() => {
    setVisitedTabs(prev => new Set([...prev, activeTab]));
  }, [activeTab]);

  useEffect(() => {
    if (consulta?.options) {
      const colorMap = {};
      consulta.options.forEach((option, index) => {
        const title = typeof option === 'string' ? option : option.title;
        colorMap[title] = OPTION_COLORS[index % OPTION_COLORS.length];
      });
      setOptionColorMap(colorMap);
    }
  }, [consulta]);

  const options = getOptionsArray(consulta);

  const handleExportPdf = async () => {
    try {
      setExportingPdf(true);
      toast.info(t('results.generatingPdfReport'));
      const response = await downloadAdminPdf(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `informe_${consulta?.title?.replace(/\s+/g, '_') || 'consulta'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(t('pdf.exportSuccess', 'PDF exportat correctament'));
    } catch {
      toast.error(t('pdf.exportError', 'Error exportant a PDF'));
    } finally {
      setExportingPdf(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6 flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-blue-400" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 md:p-8" data-testid="admin-results-page">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-8">
          <div>
            <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4 text-slate-400 hover:text-white">
              <ArrowLeft className="h-4 w-4 mr-2" />{t('common.back')}
            </Button>
            <h1 className="text-3xl font-heading font-bold text-white">{t('results.title')}</h1>
            <p className="text-slate-400 mt-1">{consulta?.title} — {consulta?.entity_name}</p>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="border-slate-600 text-slate-300 hover:text-white" data-testid="admin-export-btn" disabled={exportingPdf}>
                <Download className="h-4 w-4 mr-2" />{t('results.export')}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700">
              <DropdownMenuItem onClick={() => exportToExcel(consulta, results, i18n, t)} className="text-white hover:bg-slate-700" data-testid="admin-export-excel">
                <FileSpreadsheet className="h-4 w-4 mr-2" />Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportPdf} className="text-white hover:bg-slate-700" data-testid="admin-export-pdf">
                <FileText className="h-4 w-4 mr-2" />PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </motion.div>

        {/* Integrity check */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className={`rounded-lg p-4 mb-6 flex items-center gap-3 ${
            results?.integrity_ok ? "bg-emerald-500/10 border border-emerald-500/30" : "bg-red-500/10 border border-red-500/30"
          }`}>
          {results?.integrity_ok
            ? <CheckCircle2 className="h-5 w-5 text-emerald-400" />
            : <AlertTriangle className="h-5 w-5 text-red-400" />}
          <span className={results?.integrity_ok ? "text-emerald-300" : "text-red-300"}>
            {results?.integrity_ok ? t('results.allVotesVerified') : t('results.integrityIssues')}
          </span>
        </motion.div>

        {/* Tabs */}
        <ResultsTabs activeTab={activeTab} setActiveTab={setActiveTab} consulta={consulta} results={results} />

        {/* Tab content */}
        {visitedTabs.has('general') && (
          <div style={{ display: activeTab === 'general' ? 'block' : 'none' }}>
            <GeneralTab consulta={consulta} results={results} options={options} optionColorMap={optionColorMap} />
          </div>
        )}
        {consulta?.enable_stats_sex !== false && visitedTabs.has('sex') && (
          <div style={{ display: activeTab === 'sex' ? 'block' : 'none' }}>
            <SexTab consulta={consulta} results={results} options={options} optionColorMap={optionColorMap} />
          </div>
        )}
        {consulta?.enable_stats_age !== false && visitedTabs.has('age') && (
          <div style={{ display: activeTab === 'age' ? 'block' : 'none' }}>
            <AgeTab consulta={consulta} results={results} options={options} optionColorMap={optionColorMap} />
          </div>
        )}
        {visitedTabs.has('summary') && (
          <div style={{ display: activeTab === 'summary' ? 'block' : 'none' }}>
            <SummaryTab consulta={consulta} results={results} options={options} optionColorMap={optionColorMap} />
          </div>
        )}
        {visitedTabs.has('participation') && (
          <div style={{ display: activeTab === 'participation' ? 'block' : 'none' }}>
            <ParticipationTab results={results} />
          </div>
        )}
        {results?.blockchain_enabled && visitedTabs.has('blockchain') && (
          <div style={{ display: activeTab === 'blockchain' ? 'block' : 'none' }}>
            <BlockchainTab results={results} onRefresh={loadData} />
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
